import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * ErrorBoundary globale: intercetta gli errori di rendering dei componenti
 * ed evita lo "schermo bianco" totale, mostrando invece un messaggio
 * recuperabile. Registra l'errore in console per il debug.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log completo per diagnosi (visibile nella console del browser).
    // eslint-disable-next-line no-console
    console.error('[Vexel POS] Errore di rendering intercettato:', error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    // Ricarica la pagina per ripartire da uno stato pulito.
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message || 'Si è verificato un errore imprevisto.';
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen w-full flex items-center justify-center bg-background p-6"
      >
        <div className="max-w-md w-full rounded-2xl border border-border bg-card shadow-sm p-8 text-center">
          <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-1.5">Qualcosa è andato storto</h1>
          <p className="text-sm text-muted-foreground mb-6">
            L'app ha riscontrato un problema imprevisto. Puoi ricaricare per continuare a lavorare.
          </p>
          <button
            onClick={this.handleReload}
            data-testid="error-boundary-reload"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <RotateCcw className="h-4 w-4" /> Ricarica l'applicazione
          </button>
          {message && (
            <p className="mt-5 text-[11px] font-mono text-muted-foreground/80 break-words">
              {message}
            </p>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
